import React, { useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { PDF_WORKER_URL } from "@/config/pdfViewer";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/zoom/lib/styles/index.css";
import { KeenIcon } from "@/components";
import { Formik, Form, ErrorMessage } from "formik";
import { useSettings } from "@/providers";
import { toAbsoluteUrl } from "@/utils/Assets";
import { sanitizeRouteParam } from "@/utils";
import ReactDOM from "react-dom";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import contactService from "@/services/contact/contactService";
import { toSnake } from "@/utils";
import { LazyFormikSelect } from '@/components'
import eSignatureService from "../../services/e-signature/eSignatureService";
import { isSingleLineField, typedFieldLayout } from "./fieldSizing";

/**
 * Expected backend field shape (from sender):
 * {
 *   id, type: 'signature'|'initials'|'text'|'checkbox'|'radio'|'dropdown',
 *   page, left, top, width, height (ratios),
 *   fieldLabel?,
 *   defaultText?, placeholder?,
 *   checkbox (atomic): required?, defaultChecked?,
 *   radio (atomic): group?: string, value?: string, defaultSelected?, required?,
 *   dropdownOptions?: [{id,label,value}], dropdownDefault?: string
 * }
 */

const isImageDataUrl = (v) => typeof v === "string" && v.startsWith("data:image/png");
const SIGNABLE_FIELD_TYPES = new Set(["signature", "initials"]);

// Pull the human-readable message out of a failed sign response. The backend
// (ResponseHelper::error) returns { message: 'Failed', data: { error } } on a
// 422; some endpoints use { errors: {...} }. Surface the real message, not "Failed".
const extractSubmitError = (err) => {
  if (!err) return "";
  const candidates = [err?.data?.error, err?.error, err?.data?.errors, err?.errors];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (candidate && typeof candidate === "object") {
      const first = Object.values(candidate)
        .flat()
        .find((m) => typeof m === "string" && m.trim());
      if (first) return first.trim();
    }
  }
  return "";
};

const toLowerTrim = (value) => String(value || "").trim().toLowerCase();

const normalizeOrderNumber = (value) => {
  const n = Number(String(value ?? "").trim());
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) return null;
  return n;
};

const formatMonthDayYear = (date = new Date()) => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}-${day}-${year}`;
};

// Short, action-oriented label shown to the signer on each field spot.
// Returns "" for compact fields (checkbox/radio) so they render with no tab.
const getSignerActionLabel = (box) => {
  if (box?.type === "signature") return "Sign here";
  if (box?.type === "initials") return "Initial here";
  if (box?.placeholder === "Date Signed" || box?.type === "date_signed") return "Date";
  if (box?.placeholder === "Full Name" || box?.type === "full_name") return "Your name";
  if (box?.placeholder === "Email Address" || box?.type === "email") return "Email";
  if (box?.placeholder === "Company" || box?.type === "company") return "Company";
  if (box?.type === "dropdown") return box?.fieldLabel || "Select";
  if (box?.type === "text") return box?.fieldLabel || "Type here";
  return "";
};

// Atomic radio buttons are grouped by `group`; signer state is keyed per group.
// Sanitize the key so it's safe as a Formik/lodash path (no '.', '[', ']').
const radioGroupKey = (field) =>
  String(field?.group || `__${field?.id}`).trim().replace(/[.[\]]/g, "_") ||
  `__${field?.id}`;

const getResolvedFieldTextValue = (field, values) => {
  const typedValue = values?.text?.[field?.id];
  const normalizedFieldLabel = String(field?.fieldLabel || "").trim().toLowerCase();
  const normalizedPlaceholder = String(field?.placeholder || "").trim().toLowerCase();
  const isDateSignedTextField =
    field?.type === "date_signed" ||
    normalizedFieldLabel === "date signed" ||
    normalizedPlaceholder === "date signed";

  if (typedValue != null) {
    if (!isDateSignedTextField) return typedValue;
    if (String(typedValue).trim()) return typedValue;
  }

  if (isDateSignedTextField) {
    return field?.defaultText || formatMonthDayYear();
  }

  return field?.defaultText || "";
};

const normalizeSignerPayload = (signer) => {
  if (!signer || typeof signer !== "object") return null;
  return {
    id: String(
      signer.uuid ||
      signer.id ||
      signer.signer_id ||
      signer.recipient_id ||
      signer.signer_uuid ||
      ""
    ).trim(),
    name: String(signer.name || signer.full_name || signer.signer_name || "").trim(),
    email: String(signer.email || signer.signer_email || signer.recipient_email || "").trim(),
    order:
      normalizeOrderNumber(
        signer.order_number ||
        signer.default_order ||
        signer.sign_order ||
        signer.order
      ),
  };
};

const getFieldSigner = (field) => (
  normalizeSignerPayload(
    field?.signer ||
    field?.recipient ||
    field?.assigned_signer ||
    null
  )
);

const doesSignerMatchField = (currentSigner, field) => {
  if (!SIGNABLE_FIELD_TYPES.has(field?.type)) return true;

  const fieldSigner = getFieldSigner(field);
  if (!fieldSigner) return true;
  if (!currentSigner) return true;

  // Email is the primary and unique signer identity.
  if (currentSigner.email && fieldSigner.email) {
    return toLowerTrim(currentSigner.email) === toLowerTrim(fieldSigner.email);
  }

  // Fallbacks only when one or both emails are missing.
  if (currentSigner.id && fieldSigner.id) {
    return currentSigner.id === fieldSigner.id;
  }
  if (currentSigner.order && fieldSigner.order) {
    return currentSigner.order === fieldSigner.order;
  }
  if (currentSigner.name && fieldSigner.name) {
    return toLowerTrim(currentSigner.name) === toLowerTrim(fieldSigner.name);
  }

  return true;
};

// Util to convert signature/initials text into a tightly-cropped PNG (base64).
// The image is sized to the glyph bounds (+ a small margin) so the backend's
// aspect-ratio fit centers it cleanly in the field box instead of fitting a
// mostly-transparent canvas and shrinking the text.
const textToSignatureImage = async ({
  text,
  fontFamily,
  fontSize = 72,
  padding = 12,
}) => {
  const value = String(text || "").trim();
  if (!value) return "";

  await document.fonts.load(`${fontSize}px "${fontFamily}"`);
  await document.fonts.ready;

  // Measure the glyph bounds first so we can crop tightly.
  const measure = document.createElement("canvas").getContext("2d");
  measure.font = `${fontSize}px "${fontFamily}"`;
  const m = measure.measureText(value);

  const left = m.actualBoundingBoxLeft || 0;
  const right = m.actualBoundingBoxRight || m.width;
  const ascent = m.actualBoundingBoxAscent || fontSize * 0.8;
  const descent = m.actualBoundingBoxDescent || fontSize * 0.2;

  const textWidth = Math.ceil(left + right);
  const textHeight = Math.ceil(ascent + descent);

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, textWidth + padding * 2);
  canvas.height = Math.max(1, textHeight + padding * 2);

  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = `${fontSize}px "${fontFamily}"`;
  // Offset by the measured bounds so the glyph sits flush against the padding.
  ctx.fillText(value, padding + left, padding + ascent);

  return canvas.toDataURL("image/png");
};

const DocumentSignature = ({ title = "Sign Document" }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fields, setFields] = useState([]);
  const [activeSigner, setActiveSigner] = useState(null);
  const [isDocLoading, setIsDocLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);

  // Modes: 'type' | 'pad' | 'upload'
  const [signatureMode, setSignatureMode] = useState("pad");
  const [initialsMode, setInitialsMode] = useState("pad");

  // Previews & refs
  const [signatureImagePreview, setSignatureImagePreview] = useState(null);
  const [initialsImagePreview, setInitialsImagePreview] = useState(null);
  const sigPadRef = useRef(null);
  const initPadRef = useRef(null);

  // PDF viewer & overlay sync
  const zoomPluginInstance = zoomPlugin();
  const { ZoomIn, ZoomOut, CurrentScale } = zoomPluginInstance;
  const viewerWrapRef = useRef(null);
  const [renderTick, setRenderTick] = useState(0);
  const [currentScale, setCurrentScale] = useState(1);
  const [isPdfReady, setIsPdfReady] = useState(false);

  const { getThemeMode } = useSettings();
  const { id: rawId } = useParams();
  const id = sanitizeRouteParam(rawId);
  console.log('ID---->', id)
  const formikRef = useRef(null);
  const showPdfLoading = isDocLoading || (!!pdfUrl && !isPdfReady);

  const SIGNATURE_FONTS = [
    { label: "Pacifico", value: "Pacifico" },
    { label: "Dancing Script", value: "Dancing Script" },
    { label: "Great Vibes", value: "Great Vibes" },
    { label: "Allura", value: "Allura" },
    { label: "Alex Brush", value: "Alex Brush" },
    { label: "Satisfy", value: "Satisfy" },
    { label: "Sacramento", value: "Sacramento" },
    { label: "Caveat", value: "Caveat" },
    { label: "Indie Flower", value: "Indie Flower" },
    { label: "Yellowtail", value: "Yellowtail" },
    { label: "Courgette", value: "Courgette" },
    { label: "Patrick Hand", value: "Patrick Hand" }
  ];

  const INITIALS_FONTS = [
    { label: "Pacifico", value: "Pacifico" },
    { label: "Dancing Script", value: "Dancing Script" },
    { label: "Great Vibes", value: "Great Vibes" },
    { label: "Allura", value: "Allura" },
    { label: "Alex Brush", value: "Alex Brush" },
    { label: "Satisfy", value: "Satisfy" },
    { label: "Sacramento", value: "Sacramento" },
    { label: "Caveat", value: "Caveat" },
    { label: "Indie Flower", value: "Indie Flower" },
    { label: "Yellowtail", value: "Yellowtail" },
    { label: "Courgette", value: "Courgette" },
    { label: "Patrick Hand", value: "Patrick Hand" }
  ];


  const [signatureFont, setSignatureFont] = useState(SIGNATURE_FONTS[0].value);
  const [initialsFont, setInitialsFont] = useState(INITIALS_FONTS[0].value);

  // Sync overlaid fields organically using observer instead of manual ticks
  useEffect(() => {
    const container = viewerWrapRef.current;
    if (!container) return;

    let timeout;
    const observer = new MutationObserver(() => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        setRenderTick((n) => n + 1);
      }, 50);
    });

    observer.observe(container, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      clearTimeout(timeout);
    };
  }, [pdfUrl]);

  // Update renderTick on window resize (mobile keyboard opening, etc.)
  useEffect(() => {
    const handleResize = () => setRenderTick((n) => n + 1);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const families = [
      "Pacifico",
      "Dancing Script",
      "Great Vibes",
      "Allura",
      "Sacramento",
      "Alex Brush",
      "Yellowtail",
      "Satisfy",
      "Parisienne",
      "Caveat",
      "Indie Flower",
      "Courgette",
      "Patrick Hand",
    ];

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?" +
      families.map(f => `family=${f.replace(/ /g, "+")}`).join("&") +
      "&display=swap";

    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);



  useEffect(() => {
    if (initialsMode !== "type") return;

    const text = formikRef.current?.values?.initials_text;
    if (!text) return;

    (async () => {
      const img = await textToSignatureImage({
        text,
        fontFamily: initialsFont,
        fontSize: 64, // initials usually smaller
      });
      formikRef.current.setFieldValue("initials", img);
    })();
  }, [initialsFont, initialsMode]);


  useEffect(() => {
    if (signatureMode !== "type") return;

    const text = formikRef.current?.values?.signature_text;
    if (!text) return;

    (async () => {
      const img = await textToSignatureImage({
        text,
        fontFamily: signatureFont,
      });
      formikRef.current.setFieldValue("signature", img);
    })();
  }, [signatureFont, signatureMode]);


  // Load doc + fields
  useEffect(() => {
    let objectUrlToRevoke = null;



    const getDocumentsForSignature = async () => {
      try {
        setIsDocLoading(true);
        console.log('Hello this is a log')
        console.log('------------->', id)
        const response = await eSignatureService.getDocumentsForSignature(id);
        const base64File = response?.data.file;
        const boxData = response?.data.coordinates || response?.data.fields || [];
        const currentSignerOrder = normalizeOrderNumber(response?.data?.document?.current_signer_order);
        const signerObj =
          response?.data?.signer ||
          response?.data?.recipient_signer ||
          response?.data?.recipient ||
          response?.data?.contact ||
          null;

        if (base64File) {
          const base64Data = base64File.includes(",") ? base64File.split(",")[1] : base64File;
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: "application/pdf" });
          const fileUrl = URL.createObjectURL(blob);
          objectUrlToRevoke = fileUrl;

          setPdfUrl(fileUrl);
          setIsPdfReady(false);
          setFields(Array.isArray(boxData) ? boxData : []);
          const normalizedSigner = normalizeSignerPayload(signerObj);
          setActiveSigner({
            ...(normalizedSigner || {}),
            order: normalizedSigner?.order ?? currentSignerOrder ?? null,
          });
        }
      } catch (error) {
        console.error("Error loading document for signature:", error);
        if (error?.message === "Invalid or expired token") {
          toast.error("Invalid or expired Link.", {
            description: "Your Link has been expired. Please request again to get a new link.",
          });
          setErrorMessage(true);
        }
      } finally {
        setIsDocLoading(false);
      }
    };

    if (id) {
      setIsDocLoading(true);
      setIsPdfReady(false);
      getDocumentsForSignature();
    } else {
      setIsDocLoading(false);
      setIsPdfReady(false);
    }

    return () => {
      if (objectUrlToRevoke) URL.revokeObjectURL(objectUrlToRevoke);
    };
  }, [id]);

  // Mark PDF as ready when at least one page canvas is rendered
  useEffect(() => {
    if (!pdfUrl || !viewerWrapRef.current) {
      setIsPdfReady(false);
      return;
    }

    const hasCanvas = viewerWrapRef.current.querySelector('.rpv-core__page-layer canvas');
    if (hasCanvas) setIsPdfReady(true);
  }, [pdfUrl, renderTick]);

  // Watch DOM for page changes -> nudge overlays
  useEffect(() => {
    if (!pdfUrl || !viewerWrapRef.current) return;
    const el = viewerWrapRef.current;

    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      for (const m of mutations) {
        if ([...m.addedNodes, ...m.removedNodes].some(
          (node) => node.nodeType === 1 && node.classList?.contains('rpv-core__page-layer')
        )) {
          needsUpdate = true;
          break;
        }
      }
      if (needsUpdate) {
        setRenderTick((n) => n + 1);
      }
    });

    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pdfUrl]);

  const signerMatchedFields = useMemo(
    () => (fields || []).filter((field) => doesSignerMatchField(activeSigner, field)),
    [fields, activeSigner]
  );

  const visibleFields = useMemo(() => {
    const currentSignerOrder = normalizeOrderNumber(activeSigner?.order);

    // Order 1 sees all matching fields. Later signers only see signable fields.
    if (currentSignerOrder === 1) return signerMatchedFields;
    if (currentSignerOrder && currentSignerOrder > 1) {
      return signerMatchedFields.filter((field) => SIGNABLE_FIELD_TYPES.has(field?.type));
    }

    return signerMatchedFields;
  }, [signerMatchedFields, activeSigner]);

  // Presence flags
  const hasSignatureFields = useMemo(() => visibleFields.some((f) => f.type === "signature"), [visibleFields]);
  const hasInitialsFields = useMemo(() => visibleFields.some((f) => f.type === "initials"), [visibleFields]);

  // Quick lookup by fieldId
  const fieldById = useMemo(() => {
    const map = new Map();
    visibleFields.forEach((f) => map.set(String(f.id), f));
    return map;
  }, [visibleFields]);

  // Maps for label resolution
  const mapsByField = useMemo(() => {
    const m = {};
    visibleFields.forEach((f) => {
      if (f.type === "dropdown") {
        const v2l = {};
        (f.dropdownOptions || []).forEach((opt) => {
          const val = opt?.value ?? toSnake(opt?.label || "");
          v2l[val] = opt?.label || "";
        });
        m[f.id] = { dropdownValueToLabel: v2l };
      }
    });
    return m;
  }, [visibleFields]);

  // Formik initial values
  const initialValues = useMemo(() => {
    const init = {
      signature: "",        // final PNG
      signature_text: "",   // typed text
      initials: "",         // final PNG
      initials_text: "",    // typed text
      signatory_name: "",
      text: {},
      checkbox: {},
      radio: {},
      dropdown: {},
    };

    visibleFields.forEach((f) => {
      if (f.type === "text") {
        init.text[f.id] = f.defaultText || "";
      } else if (f.type === "checkbox") {
        // atomic checkbox -> single boolean
        init.checkbox[f.id] = !!f.defaultChecked;
      } else if (f.type === "radio") {
        // atomic radio -> state is keyed by group; a default-selected button
        // pre-selects its value for the whole group.
        const key = radioGroupKey(f);
        if (init.radio[key] == null) init.radio[key] = "";
        if (f.defaultSelected) init.radio[key] = f.value ?? "";
      } else if (f.type === "dropdown") {
        const first = (f.dropdownOptions || [])[0];
        const explicit = (f.dropdownOptions || []).find((opt) => opt.defaultSelected);
        init.dropdown[f.id] =
          (explicit ? (explicit.value ?? toSnake(explicit.label || "")) : undefined) ??
          f.dropdownDefault ??
          (first ? (first.value ?? toSnake(first.label || "")) : "");
      }
    });
    return init;
  }, [visibleFields]);

  // Custom validity for submit button
  const isSignatureValid = (v) =>
    signatureMode === "type" ? !!(v && String(v).trim()) : isImageDataUrl(v);
  const isInitialsValid = (v) =>
    initialsMode === "type" ? !!(v && String(v).trim()) : isImageDataUrl(v);

  // Overlay placement helper
  const placeIntoPage = (box, content) => {
    void renderTick;
    void currentScale;

    // console.log('content', content)


    // const pageLayers = document.querySelectorAll('.rpv-core__page-layer');
    // if (!pageLayers.length) return null;

    // // Use box.page as 1-based index (sender ensures this)
    // const pageIndex = Math.max(0, (box?.page ?? 1) - 1);
    // const pageElement = pageLayers[pageIndex];

    const pageElement = document.querySelector(
      `.rpv-core__page-layer[data-virtual-index="${box.page}"]`
    );
    if (!pageElement) return null;

    const canvas = pageElement.querySelector('canvas');
    if (!canvas) return null;

    const scaleFix = canvas.width / pageElement.offsetWidth;

    const left = Math.max(0, Math.min(Number(box.left) || 0, 1));
    const top = Math.max(0, Math.min(Number(box.top) || 0, 1));
    const width = Math.max(0, Math.min(Number(box.width) || 0, 1 - left));
    const height = Math.max(0, Math.min(Number(box.height) || 0, 1 - top));

    // Signer-facing styling is intentionally lighter than the sender's builder:
    // a thin dashed outline + faint tint, with a small action tab — not a heavy
    // highlighted block. Compact controls (checkbox/radio) get just the outline.
    const isCompact = box.type === 'checkbox' || box.type === 'radio';
    const isSignable = box.type === 'signature' || box.type === 'initials';
    const actionLabel = getSignerActionLabel(box);
    const showTab = !isCompact && !!actionLabel;
    const heightPx = height * (canvas.height / scaleFix);

    const style = {
      position: 'absolute',
      top: `${top * (canvas.height / scaleFix)}px`,
      left: `${left * (canvas.width / scaleFix)}px`,
      width: `${width * (canvas.width / scaleFix)}px`,
      // Fixed height (not minHeight + fit-content) so the signer sees the exact box
      // the sender placed and the backend stamps by ratio — all three stages agree.
      height: `${heightPx}px`,
      zIndex: 999,
      pointerEvents: 'auto',
    };

    const boxStyle = {
      border: '1.5px dashed var(--tw-primary)',
      borderRadius: 6,
      overflow: 'hidden',
      backgroundColor: isCompact
        ? 'transparent'
        : isSignable
          ? 'var(--tw-primary-clarity)'
          : 'rgba(255,255,255,0.92)',
      cursor: isSignable ? 'pointer' : 'default',
    };

    // The tab sits just above the box, so a one-line box keeps all its height
    // for the value. Inside, it hid the typed text of short fields (OL-238).
    return ReactDOM.createPortal(
      <div key={box.id} style={style}>
        {showTab && (
          <span className="absolute bottom-full left-0 z-10 flex items-center h-[15px] px-1 bg-primary text-white text-[9px] leading-none whitespace-nowrap rounded-t-md">
            {actionLabel}
          </span>
        )}
        <div className="w-full h-full" style={boxStyle}>
          {typeof content === 'function' ? content(heightPx) : content}
        </div>
      </div>,
      pageElement
    );
  };

  // A typed field filling its box: one line with a font that fits short boxes,
  // a textarea only when the box has room for more lines (OL-238).
  const renderTypedInput = ({ heightPx, singleLine, value, onChange, placeholder, disabled = false }) => {
    const { multiline, fontSize, lineHeight } = typedFieldLayout(heightPx, { singleLine });
    const Field = multiline ? "textarea" : "input";
    return (
      <div className="w-full h-full flex items-center bg-white">
        <Field
          {...(multiline ? {} : { type: "text" })}
          className={`w-full ${multiline ? "h-full py-0.5 resize-none" : ""} px-1 text-black outline-none bg-transparent`}
          style={{ fontSize: `${fontSize}px`, lineHeight }}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    );
  };

  // Render overlays
  const renderOverlays = (formik) => {
    if (!visibleFields?.length) return null;

    return visibleFields.map((box) => {
      if (box.type === "signature" || box.type === "initials") {
        const val = box.type === "signature" ? formik.values.signature : formik.values.initials;
        const hasValue = !!(val && String(val).trim());
        // Signatures left-align (start from the left of the box, matching the stamped
        // PDF); initials stay centered in their square box.
        const alignClass = box.type === "signature" ? "justify-start" : "justify-center";
        const content = (
          <div className="w-full h-full flex flex-col">
            <div className={`flex-1 flex items-center ${alignClass} px-1 py-1 overflow-hidden`}>
              {hasValue ? (
                isImageDataUrl(val) ? (
                  <img src={val} alt="" className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-[12px] text-black truncate">{val}</span>
                )
              ) : (
                <span className="flex items-center gap-1 text-[11px] font-medium text-primary select-none">
                  <span aria-hidden>✎</span>
                  {box.type === "signature" ? "Sign here" : "Initial here"}
                </span>
              )}
            </div>
          </div>
        );
        return placeIntoPage(box, content);
      }

      if (box.type === "text") {
        const isDateSigned = box.placeholder === 'Date Signed';
        return placeIntoPage(box, (heightPx) => renderTypedInput({
          heightPx,
          singleLine: isSingleLineField(box),
          value: isDateSigned ? formatMonthDayYear() : (formik.values.text?.[box.id] ?? ""),
          onChange: isDateSigned ? () => { } : (e) => formik.setFieldValue(`text.${box.id}`, e.target.value),
          placeholder: box.placeholder || "Type here...",
          disabled: box.disabled || isDateSigned,
        }));
      }

      if (box.type === "checkbox") {
        // atomic checkbox -> a single toggle overlaid on the document's printed box
        const checked = !!formik.values.checkbox?.[box.id];
        return placeIntoPage(
          box,
          <div className="w-full h-full flex items-center justify-center bg-transparent">
            <input
              className="checkbox checkbox-sm checked:bg-primary dark:checked:bg-primary cursor-pointer"
              type="checkbox"
              checked={checked}
              onChange={(e) => formik.setFieldValue(`checkbox.${box.id}`, e.target.checked)}
            />
          </div>
        );
      }

      if (box.type === "radio") {
        // atomic radio -> a single button; selection is shared across its group
        const key = radioGroupKey(box);
        const selected = (formik.values.radio?.[key] ?? "") === (box.value ?? "");
        return placeIntoPage(
          box,
          <div className="w-full h-full flex items-center justify-center bg-transparent">
            <input
              className="radio radio-sm cursor-pointer"
              type="radio"
              name={`radio-${key}`}
              checked={selected}
              onChange={() => formik.setFieldValue(`radio.${key}`, box.value ?? "")}
            />
          </div>
        );
      }

      if (box.type === "dropdown") {
        const value = formik.values.dropdown?.[box.id] ?? (box.dropdownDefault || "");
        const setVal = (v) => formik.setFieldValue(`dropdown.${box.id}`, v);
        return placeIntoPage(
          box,
          <div className="w-full h-full flex flex-col bg-white text-[11px] overflow-auto">
            <div className="px-2 pb-[4px]">
              <select className="w-full bg-transparent select select-sm cursor-pointer text-black" value={value} onChange={(e) => setVal(e.target.value)}>
                {(box.dropdownOptions || []).map((opt) => (
                  <option key={opt.id} value={opt.value ?? toSnake(opt.label || "")} className="text-black">
                    {opt.label}
                  </option>
                ))}
              </select>
              <ErrorMessage
                name={`dropdown.${box.id}`}
                component="div"
                className="mt-1 text-[9px] text-red-600 h-fit leading-[1.3em]"
              />
            </div>
          </div>
        );
      }

      if (["date_signed", "full_name", "email", "company"].includes(box.type)) {
        const value = getResolvedFieldTextValue(box, formik.values);
        const label =
          box.fieldLabel ||
          (box.type === "date_signed"
            ? "Date Signed"
            : box.type === "full_name"
              ? "Full Name"
              : box.type === "email"
                ? "Email Address"
                : "Company");

        return placeIntoPage(box, (heightPx) => renderTypedInput({
          heightPx,
          singleLine: true,
          value,
          onChange: (e) => {
            // live update in memory so it saves correctly on submit
            box.defaultText = e.target.value;
            formik.setFieldValue(`text.${box.id}`, e.target.value);
          },
          placeholder: label,
        }));
      }

      return null;
    });
  };

  // Build the coordinates payload the backend expects (mirror of fetched fields)
  const buildCoordinatesPayload = (fields, values, uiVisibleFields = fields) => {
    const visibleFieldIds = new Set((uiVisibleFields || []).map((f) => String(f.id)));

    return (fields || []).map((f) => {
      const out = { ...f }; // keep page/left/top/width/height/etc
      const isVisibleInUi = visibleFieldIds.has(String(f.id));

      // Hidden fields should still be sent, but untouched.
      if (!isVisibleInUi) return out;

      if (f.type === "text" || ["date_signed", "full_name", "email", "company"].includes(f.type)) {
        out.defaultText = getResolvedFieldTextValue(f, values);
      }

      if (f.type === "checkbox") {
        // atomic checkbox -> single boolean the signer toggled
        out.checked = !!values?.checkbox?.[f.id];
      }

      if (f.type === "radio") {
        // atomic radio -> this button is selected when the group's chosen value
        // matches this button's value
        const key = radioGroupKey(f);
        const chosen = values?.radio?.[key] ?? "";
        out.selected = chosen !== "" && chosen === (f.value ?? "");
      }

      if (f.type === "dropdown") {
        // set dropdown selection both as scalar and per-option defaultSelected
        const picked = values?.dropdown?.[f.id];
        const fallbackSelected = (f.dropdownOptions || []).find((opt) => opt.defaultSelected);
        const chosen =
          picked ??
          (fallbackSelected ? (fallbackSelected.value ?? toSnake(fallbackSelected.label || "")) : undefined) ??
          f.dropdownDefault ??
          ((f.dropdownOptions || [])[0]?.value ?? toSnake((f.dropdownOptions || [])[0]?.label || ""));

        out.dropdownDefault = chosen;
        out.dropdownOptions = (f.dropdownOptions || []).map((opt) => {
          const val = opt?.value ?? toSnake(opt?.label || "");
          return { ...opt, defaultSelected: val === chosen };
        });
      }

      if (f.type === "signature") {
        // value can be typed string OR data URL (pad/upload)
        out.value = values?.signature || "";
      }

      if (f.type === "initials") {
        // value can be typed string OR data URL (pad/upload)
        out.value = values?.initials || "";
      }
      // console.log('OUT', out)
      return out;
    });
  };

  const handleSubmit = async (values) => {
    console.log('values', values)
    // return
    setUploading(true);
    try {
      // Put every user input back into the same coordinates structure
      const coordinates = buildCoordinatesPayload(signerMatchedFields, values, visibleFields);
      // return console.log({ coordinates });

      const payload = {
        values: coordinates,
        signatory_name: values.signatory_name
      };

      const { status } = await eSignatureService.uploadSignedDocument(id, payload);
      if (status) {
        toast.success("Document submitted.", {
          description: "Thank you! Your inputs have been submitted successfully.",
        });
        setSuccessMessage(true);
      }
    } catch (error) {
      const msg = extractSubmitError(error);
      console.error("Error submitting signed document:", msg || error);
      toast.error("Submission failed.", {
        description: msg || "Something went wrong. Please review your entries and try again.",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="custom-scrollbar max-h-[98vh] scrollable-y-auto bg-[--tw-navbar-bg] dark:bg-[--tw-navbar-bg-dark] border border-gray-300 dark:border-t-light dark:border-light p-6 rounded-lg shadow-lg container-fixed relative">
        <h3 className="capitalize card-title">{title}</h3>

        {successMessage ? (
          <div className="w-full text-center border-2 border-dashed my-5 p-5 h-[150px] flex items-center justify-center flex-col text-sm rounded-md transition border-success bg-success-light">
            <p className="text-xl font-medium text-success-active">Document submitted successfully!</p>
            <p className="mt-1 text-xs">Thank you! You may now close this window.</p>
          </div>
        ) : errorMessage ? (
          <div className="w-full text-center border-2 border-dashed my-5 p-5 h-[150px] flex items-center justify-center flex-col text-sm rounded-md transition border-danger bg-danger-light">
            <p className="text-xl font-medium text-danger-active">Invalid or expired link used!</p>
            <p className="mt-1 text-xs">Please request a new link. You may close this window.</p>
          </div>
        ) : (
          <Formik
            innerRef={formikRef}
            enableReinitialize
            initialValues={initialValues}
            // UI-level validation via disabled button; backend has final validation
            validate={(vals) => {
              const errors = {};

              // Signatures
              if (hasSignatureFields && !isSignatureValid(vals.signature)) {
                errors.signature =
                  signatureMode === "type"
                    ? "Please type your signature."
                    : "Please draw/upload a signature (.png).";
              }

              if (!vals.signatory_name.trim()) {
                errors.signatory_name = "Signatory Name is required";
              }

              if (hasInitialsFields && !isInitialsValid(vals.initials)) {
                errors.initials =
                  initialsMode === "type"
                    ? "Please type your initials."
                    : "Please draw initials.";
              }

              // Typed initials must follow the e-signature standard (mirrors backend):
              // up to 6 letters, optionally separated by periods, spaces or hyphens.
              if (hasInitialsFields && initialsMode === "type" && !errors.initials) {
                const typed = String(vals.initials_text || "").trim();
                if (typed.length > 6) {
                  errors.initials = "Initials must be 6 characters or fewer.";
                } else if (typed && !/^\p{L}[\p{L}.\s-]*$/u.test(typed)) {
                  errors.initials =
                    "Initials may only contain letters, periods, spaces or hyphens.";
                }
              }

              // Dynamic fields
              const requiredRadioGroups = {};
              const filledRadioGroups = {};
              visibleFields.forEach((f) => {
                // if (f.type === "text" && !vals.text?.[f.id]?.trim()) {
                //   errors.text = errors.text || {};
                //   errors.text[f.id] = `${f.fieldLabel || "Text field"} is required`;
                // }
                if (f.type === "checkbox") {
                  // atomic checkbox -> optional unless the sender explicitly
                  // marked it required; signers check only what applies.
                  if (f.required === true && !vals.checkbox?.[f.id]) {
                    errors.checkbox = errors.checkbox || {};
                    errors.checkbox[f.id] = "This checkbox is required";
                  }
                }
                if (f.type === "radio") {
                  // atomic radio -> a group is required if any of its buttons is;
                  // validate one selection per required group.
                  const key = radioGroupKey(f);
                  if (f.required !== false) requiredRadioGroups[key] = true;
                  if (vals.radio?.[key]) filledRadioGroups[key] = true;
                }
                if (f.type === "dropdown" && !vals.dropdown?.[f.id]) {
                  errors.dropdown = errors.dropdown || {};
                  errors.dropdown[f.id] = `${f.fieldLabel || "Dropdown"} is required`;
                }
              });
              Object.keys(requiredRadioGroups).forEach((key) => {
                if (!filledRadioGroups[key]) {
                  errors.radio = errors.radio || {};
                  errors.radio[key] = "Please select an option";
                }
              });

              // Global toast if anything failed
              // if (Object.keys(errors).length > 0) {
              //   toast.error("Please fill all required fields", {
              //     description: "Some fields are missing or invalid.",
              //   });
              // }
              console.log("VALIDATE ERRORS:", errors);
              return errors;
            }}

            // validate={(vals) => {
            //   const errors = {};
            //   if (hasSignatureFields && !isSignatureValid(vals.signature)) {
            //     errors.signature = signatureMode === "type"
            //       ? "Please type your signature."
            //       : "Please draw/upload a signature (.png).";
            //   }
            //   if (hasInitialsFields && !isInitialsValid(vals.initials)) {
            //     errors.initials = initialsMode === "type"
            //       ? "Please type your initials."
            //       : "Please draw/upload initials (.png).";
            //   }
            //   return errors;
            // }}
            onSubmit={
              handleSubmit
              // console.log("ON SUBMIT CALLED!");              
            }
          >
            {(formik) => {
              useEffect(() => {
                formik.validateForm();
              }, [signatureMode]);

              useEffect(() => {
                formik.validateForm();
              }, [initialsMode]);

              // Restoration logic for signature/initials pads (especially for mobile resize persistence)
              useEffect(() => {
                if (signatureMode === 'pad' && sigPadRef.current && formik.values.signature) {
                  if (sigPadRef.current.isEmpty()) {
                    sigPadRef.current.fromDataURL(formik.values.signature);
                  }
                }
              }, [signatureMode, formik.values.signature, renderTick]);

              useEffect(() => {
                if (initialsMode === 'pad' && initPadRef.current && formik.values.initials) {
                  if (initPadRef.current.isEmpty()) {
                    initPadRef.current.fromDataURL(formik.values.initials);
                  }
                }
              }, [initialsMode, formik.values.initials, renderTick]);
              return (
                <Form>
                  <div className="relative">
                    {showPdfLoading && !pdfUrl && (
                      <div className="w-full h-full bg-white/50 rounded-md mt-5 dark:bg-dark/80 backdrop-blur-sm flex items-start">
                        <div className="w-full px-6 py-6 animate-pulse space-y-4">
                          <div className="h-6 w-1/3 rounded bg-gray-200 dark:bg-dark" />
                          <div className="h-[360px] w-full rounded bg-gray-200 dark:bg-dark" />
                          <div className="h-5 w-40 rounded bg-gray-200 dark:bg-dark" />
                          <div className="h-10 w-full rounded bg-gray-200 dark:bg-dark" />
                          <div className="h-5 w-36 rounded bg-gray-200 dark:bg-dark" />
                          <div className="h-32 w-full rounded bg-gray-200 dark:bg-dark" />
                        </div>
                      </div>
                    )}
                    {pdfUrl && (
                      <div className="my-4">
                        <div className="flex items-center justify-end mb-2.5 gap-1.5">
                          <div className="flex items-center justify-between w-full">
                            <div>Preview Document</div>
                            <div className="flex items-center gap-1.5">
                              <ZoomIn>
                                {(props) => (
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-light"
                                    onClick={props.onClick}
                                    disabled={props.isDisabled}
                                  >
                                    <KeenIcon icon="plus" />
                                  </button>
                                )}
                              </ZoomIn>

                              <CurrentScale>
                                {(props) => {
                                  useEffect(() => setCurrentScale(props.scale), [props.scale]);
                                  return <span className="text-xs">{Math.round(props.scale * 100)}%</span>;
                                }}
                              </CurrentScale>

                              <ZoomOut>
                                {(props) => (
                                  <button
                                    type="button"
                                    className="btn btn-xs btn-light"
                                    onClick={props.onClick}
                                    disabled={props.isDisabled}
                                  >
                                    <KeenIcon icon="minus" />
                                  </button>
                                )}
                              </ZoomOut>
                            </div>
                          </div>
                        </div>

                        <div ref={viewerWrapRef} className="h-[480px] overflow-hidden border rounded">
                          <Worker workerUrl={PDF_WORKER_URL}>
                            <Viewer
                              fileUrl={pdfUrl}
                              defaultScale={1}
                              plugins={[zoomPluginInstance]}
                              onDocumentLoad={(e) => {
                                setIsPdfReady(false);
                                setRenderTick((n) => n + 1);
                              }}
                              onPageChange={() => setRenderTick((n) => n + 1)}
                            />
                          </Worker>

                          {renderOverlays(formik)}
                        </div>
                      </div>
                    )}

                    {/* Signature + Initials input methods */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {hasSignatureFields && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium">Signature input</p>
                            {/* Replace your signature btn-group with this (if you have one) */}
                            <div className="relative w-[240px] input rounded-md p-1 flex items-center h-[36px]">
                              <div
                                className="absolute top-1 bottom-1 left-1 badge badge-primary badge-outline rounded-md transition-transform duration-300"
                                style={{
                                  width: "calc((100% - 0.5rem) / 3)",
                                  transform: `translateX(${signatureMode === "type" ? 0 : signatureMode === "pad" ? 100 : 200}%)`,
                                }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setSignatureMode("type");
                                  setSignatureImagePreview(null);
                                  sigPadRef.current?.clear?.();
                                  formik.setFieldValue("signature", ""); // plain text later
                                }}
                                className={`relative flex-1 text-center text-xs h-[28px] leading-4 transition-colors ${signatureMode === "type" ? "text-primary" : "text-gray-600"
                                  }`}
                              >
                                Type
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSignatureMode("pad");
                                  setSignatureImagePreview(null);
                                  formik.setFieldValue("signature", ""); // PNG from pad
                                }}
                                className={`relative flex-1 text-center text-xs h-[28px] leading-4 transition-colors ${signatureMode === "pad" ? "text-primary" : "text-gray-600"
                                  }`}
                              >
                                Pad
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setSignatureMode("upload");
                                  sigPadRef.current?.clear?.();
                                  formik.setFieldValue("signature", ""); // PNG from upload
                                }}
                                className={`relative flex-1 text-center text-xs h-[28px] leading-4 transition-colors ${signatureMode === "upload" ? "text-primary" : "text-gray-600"
                                  }`}
                              >
                                Upload
                              </button>
                            </div>

                          </div>

                          {signatureMode === "type" && (
                            <div className="space-y-2">
                              {/* Font selector */}
                              <div className="mt-3">
                                <label htmlFor="" className="form-label mb-1">Select font style for signature text</label>
                                <LazyFormikSelect
                                  name="signatureFont"
                                  placeholder="Select Signature Font"
                                  options={SIGNATURE_FONTS}
                                  isMulti={false}
                                  creatable={false}
                                  returnFullObject={false}
                                  getOptionLabel={(opt) => opt.label}
                                  getOptionValue={(opt) => opt.value}
                                  onChange={(selected) => setSignatureFont(selected?.value)}
                                />
                              </div>

                              {/* Text input (TEXT ONLY) */}
                              <input
                                className="input input-sm w-full"
                                placeholder="Type your full name"
                                maxLength={100}
                                value={formik.values.signature_text}
                                onChange={async (e) => {
                                  const text = e.target.value;
                                  formik.setFieldValue("signature_text", text);

                                  if (!text.trim()) {
                                    formik.setFieldValue("signature", "");
                                    return;
                                  }

                                  const img = await textToSignatureImage({
                                    text,
                                    fontFamily: signatureFont,
                                  });

                                  formik.setFieldValue("signature", img);
                                }}
                              />

                              {/* Preview */}
                              {formik.values.signature && (
                                <img
                                  src={formik.values.signature}
                                  alt="Signature Preview"
                                  className="border rounded max-h-32 bg-input w-full mt-2"
                                />
                              )}

                              <ErrorMessage name="signature" component="div" className="text-xs text-red-600" />

                            </div>
                          )}

                          {signatureMode === "pad" && (
                            <div>
                              <SignatureCanvas
                                penColor="black"
                                canvasProps={{
                                  className:
                                    "cursor-crosshair relative h-[130px] w-full card border-2 border-dashed border-brand-clarity",
                                }}
                                ref={sigPadRef}
                                onEnd={() => {
                                  const data = sigPadRef.current?.getCanvas().toDataURL("image/png");
                                  formik.setFieldValue("signature", data);
                                }}
                              />
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => {
                                    sigPadRef.current?.clear();
                                    formik.setFieldValue("signature", "");
                                  }}
                                  className="mt-2 btn btn-sm btn-link"
                                >
                                  Clear
                                </button>
                                <ErrorMessage name="signature" component="div" className="mt-1 text-xs text-red-600" />
                              </div>
                            </div>
                          )}

                          {signatureMode === "upload" && (
                            <div className="min-h-[155px]">
                              <label
                                className="cursor-pointer relative h-[130px] w-full card border-2 border-dashed border-brand-clarity bg-center bg-[length:600px] bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    getThemeMode() === "dark"
                                      ? `url('${toAbsoluteUrl("/media/images/2600x1200/bg-4-dark.png")}')`
                                      : `url('${toAbsoluteUrl("/media/images/2600x1200/bg-4.png")}')`,
                                }}
                              >
                                <input
                                  className="sr-only absolute w-full h-full !cursor-pointer"
                                  type="file"
                                  accept=".png"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.type !== "image/png") {
                                      setSignatureImagePreview(null);
                                      formik.setFieldValue("signature", "");
                                      return;
                                    }
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                      const imageDataUrl = reader.result;
                                      setSignatureImagePreview(imageDataUrl);
                                      formik.setFieldValue("signature", imageDataUrl);
                                    };
                                    reader.readAsDataURL(file);
                                  }}
                                />
                                <div className="grid items-center card-body">
                                  <div className="flex flex-col gap-3">
                                    <div className="flex flex-col text-center">
                                      <span className="mb-px text-lg font-medium text-gray-900">Upload your signature</span>
                                      <span className="text-gray-700 text-2sm">Supported file types: .png</span>
                                    </div>
                                  </div>
                                </div>
                              </label>
                              {signatureImagePreview && (
                                <img src={signatureImagePreview} alt="Signature Preview" className="mt-2 border rounded max-h-32" />
                              )}
                              <div className="flex items-center justify-end h-[25px]">
                                <ErrorMessage name="signature" component="div" className="text-xs text-red-600" />
                              </div>
                            </div>
                          )}

                        </div>
                      )}
                      {!showPdfLoading && (
                        <div>
                          <p className="text-sm font-medium mt-3 mb-2">Signatory Name <span className="pl-px text-red-600">*</span></p>
                          <input
                            name="signatory_name"
                            className="input input-sm w-full"
                            placeholder="Type signatory name here"
                            value={formik.values.signatory_name}
                            onBlur={formik.handleBlur}
                            onChange={(e) => formik.setFieldValue("signatory_name", e.target.value)}
                          />
                          <ErrorMessage name="signatory_name" component="div" className="mt-1 text-xs text-red-600" />
                        </div>
                      )}

                      {hasInitialsFields && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-medium">Initials input</p>
                            {/* Replace your initials btn-group with this */}
                            <div className="relative w-[240px] input rounded-md p-1 flex items-center h-[36px]">
                              {/* Sliding pill */}
                              <div
                                className="absolute top-1 bottom-1 left-1 badge badge-primary badge-outline rounded-md transition-transform duration-300"
                                style={{
                                  width: "calc((100% - 0.5rem) / 2)", // 3 segments
                                  transform: `translateX(${initialsMode === "type" ? 0 : initialsMode === "pad" ? 100 : 200}%)`,
                                }}
                              />
                              {/* Type */}
                              <button
                                type="button"
                                onClick={() => {
                                  setInitialsMode("type");
                                  setInitialsImagePreview(null);
                                  initPadRef.current?.clear?.();
                                  formik.setFieldValue("initials", ""); // plain string will be typed elsewhere
                                }}
                                className={`relative flex-1 text-center text-xs h-[28px] leading-4 transition-colors ${initialsMode === "type" ? "text-primary" : "text-gray-600"
                                  }`}
                              >
                                Type
                              </button>
                              {/* Pad */}
                              <button
                                type="button"
                                onClick={() => {
                                  setInitialsMode("pad");
                                  setInitialsImagePreview(null);
                                  formik.setFieldValue("initials", ""); // will be PNG data URL from pad
                                }}
                                className={`relative flex-1 text-center text-xs h-[28px] leading-4 transition-colors ${initialsMode === "pad" ? "text-primary" : "text-gray-600"
                                  }`}
                              >
                                Pad
                              </button>
                              { /* <button
                              type="button"
                              onClick={() => {
                                setInitialsMode("upload");
                                initPadRef.current?.clear?.();
                                formik.setFieldValue("initials", ""); // will be PNG data URL from upload
                              }}
                              className={`relative flex-1 text-center text-xs h-[28px] leading-4 transition-colors ${initialsMode === "upload" ? "text-primary" : "text-gray-600"
                                }`}
                            >
                              Upload
                            </button>*/}
                            </div>

                          </div>

                          {initialsMode === "type" && (
                            <div className="mb-1">
                              <div className="mb-3">
                                <label htmlFor="" className="form-label mb-1">Select font style for Initials text</label>
                                <LazyFormikSelect
                                  name="initialsFont"
                                  placeholder="Select Signature Font"
                                  options={INITIALS_FONTS}
                                  isMulti={false}
                                  creatable={false}
                                  returnFullObject={false}
                                  getOptionLabel={(opt) => opt.label}
                                  getOptionValue={(opt) => opt.value}
                                  onChange={(selected) => setInitialsFont(selected?.value)}
                                />
                                {/* <select
                                className="select select-sm cursor-pointer w-full"
                                value={initialsFont}
                                onChange={(e) => setInitialsFont(e.target.value)}
                              >
                                {INITIALS_FONTS.map(f => (
                                  <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                              </select> */}
                              </div>
                              <input
                                className="input input-sm w-full"
                                placeholder="Type your initials"
                                maxLength={6}
                                value={formik.values.initials_text}
                                onChange={async (e) => {
                                  // Initials standard: a few letters, optionally separated by
                                  // a period, space or hyphen (max 6) — mirrors backend validation.
                                  const text = e.target.value
                                    .replace(/[^\p{L}.\s-]/gu, "")
                                    .slice(0, 6);
                                  formik.setFieldValue("initials_text", text);

                                  if (!text.trim()) {
                                    formik.setFieldValue("initials", "");
                                    return;
                                  }

                                  const img = await textToSignatureImage({
                                    text,
                                    fontFamily: initialsFont,
                                    fontSize: 64,
                                  });

                                  formik.setFieldValue("initials", img);
                                }}
                              />

                              {formik.values.initials && (
                                <img
                                  src={formik.values.initials}
                                  alt="Signature Preview"
                                  className="border rounded max-h-32 bg-input w-full mt-2"
                                />
                              )}
                              <ErrorMessage name="initials" component="div" className="mt-1 text-xs text-red-600" />
                            </div>
                          )}

                          {initialsMode === "pad" && (
                            <div>
                              <SignatureCanvas
                                penColor="black"
                                canvasProps={{
                                  className:
                                    "cursor-crosshair relative h-[130px] w-full card border-2 border-dashed border-brand-clarity",
                                }}
                                ref={initPadRef}
                                onEnd={() => {
                                  const data = initPadRef.current?.getCanvas().toDataURL("image/png");
                                  formik.setFieldValue("initials", data);
                                }}
                              />
                              <div className="flex items-center justify-between">
                                <button
                                  type="button"
                                  onClick={() => {
                                    initPadRef.current?.clear();
                                    formik.setFieldValue("initials", "");
                                  }}
                                  className="mt-2 btn btn-sm btn-link"
                                >
                                  Clear
                                </button>
                                <ErrorMessage name="initials" component="div" className="mt-1 text-xs text-red-600" />
                              </div>
                            </div>
                          )}

                          {initialsMode === "upload" && (
                            <div className="min-h-[155px]">
                              <label
                                className="cursor-pointer relative h-[130px] w-full card border-2 border-dashed border-brand-clarity bg-center bg-[length:600px] bg-no-repeat"
                                style={{
                                  backgroundImage:
                                    getThemeMode() === "dark"
                                      ? `url('${toAbsoluteUrl("/media/images/2600x1200/bg-4-dark.png")}')`
                                      : `url('${toAbsoluteUrl("/media/images/2600x1200/bg-4.png")}')`,
                                }}
                              >
                                <input
                                  className="sr-only absolute w-full h-full !cursor-pointer"
                                  type="file"
                                  accept=".png"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.type !== "image/png") {
                                      setInitialsImagePreview(null);
                                      formik.setFieldValue("initials", "");
                                      return;
                                    }
                                    const reader = new FileReader();
                                    reader.onload = () => {
                                      const imageDataUrl = reader.result;
                                      setInitialsImagePreview(imageDataUrl);
                                      formik.setFieldValue("initials", imageDataUrl);
                                    };
                                    reader.readAsDataURL(file);
                                  }}
                                />
                                <div className="grid items-center card-body">
                                  <div className="flex flex-col gap-3">
                                    <div className="flex flex-col text-center">
                                      <span className="mb-px text-lg font-medium text-gray-900">Upload your initials</span>
                                      <span className="text-gray-700 text-2sm">Supported file types: .png</span>
                                    </div>
                                  </div>
                                </div>
                              </label>
                              {initialsImagePreview && (
                                <img src={initialsImagePreview} alt="Initials Preview" className="mt-2 border rounded max-h-32" />
                              )}
                              <div className="flex items-center justify-end h-[25px]">
                                <ErrorMessage name="initials" component="div" className="text-xs text-red-600" />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                  </div>

                  <div className="flex items-center justify-end gap-2 mt-6">
                    <button
                      type="submit"
                      disabled={
                        uploading ||
                        (hasSignatureFields && !isSignatureValid(formik.values.signature)) ||
                        (hasInitialsFields && !isInitialsValid(formik.values.initials))
                      }
                      className="btn btn-sm btn-primary"
                    >
                      {uploading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </Form>
              )
            }}
          </Formik>
        )}
      </div>
    </div>
  );
};

export { DocumentSignature };
